import { useEffect, useState } from "react";
import {
  TableData,
  SortConfig,
} from "@/App/ReconciliationFinancialTransactions/Table";
import { AddReconciliationBtn } from "@/App/ReconciliationFinancialTransactions/Add";
import { getFinancialReconciliationTransactionsData } from "@/Api/reconcialiationFinancialTransactions";
import { FinancialTransactionProps } from "@/types/financialTransaction";
import { CreateLoaderTable } from "@/components/Loaders";
import { Main } from "@/components/Main";
import { ApiDataProps } from "@/pages/Tenants";
import { Filters } from "@/App/ReconciliationFinancialTransactions/Filters";
import { CircleDollarSign } from "lucide-react";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";

export const PageList = () => {
  const [reconciliation, setReconciliation] = useState({
    data: [],
    current_page: 1,
    last_page: 1,
    next_page_url: null,
    prev_page_url: null,
    from: 1,
    to: 1,
    total: 0,
    per_page: 10,
  } as ApiDataProps<FinancialTransactionProps>);
  const [state, setState] = useState("loading");
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    column: "start_date",
    direction: "desc",
  });
  const { hasPermission } = usePermissions();

  const fetchData = async (
    page: number = 1,
    currentFilters = filters,
    currentSort = sortConfig
  ) => {
    setState("loading");
    try {
      const { data } = await getFinancialReconciliationTransactionsData(page, {
        ...currentFilters,
        order_by: currentSort.column,
        order_direction: currentSort.direction,
      });
      setReconciliation(data?.response);
    } catch (error) {
      console.error("Erro ao buscar dados:", error);
    } finally {
      setState("production");
    }
  };

  const pageActions = {
    next: async () => {
      if (reconciliation?.current_page < reconciliation?.last_page) {
        await fetchData(reconciliation?.current_page + 1);
      }
    },
    previous: async () => {
      if (reconciliation?.current_page > 1) {
        await fetchData(reconciliation?.current_page - 1);
      }
    },
    lastPage: async () => {
      if (reconciliation?.current_page !== reconciliation?.last_page) {
        await fetchData(reconciliation?.last_page);
      }
    },
  };

  const handleFilter = async (filterData: any) => {
    setFilters(filterData);
    await fetchData(1, filterData);
  };

  const handleSort = async (column: string, direction: "asc" | "desc") => {
    const newSort = { column, direction };
    setSortConfig(newSort);
    await fetchData(1, filters, newSort);
  };

  useEffect(() => {
    fetchData(1);
  }, []);

  return (
    <Main>
      <If condition={hasPermission("view-reconciliations-list")}>
        <div className="flex justify-center md:justify-between items-center flex-wrap gap-2 mb-4">
          <h2 className="text-2xl font-semibold flex gap-2 items-center">
            <CircleDollarSign className="h-6 w-6" />
            Conciliação Financeira
          </h2>
          <div className="flex items-center gap-2">
            <If condition={hasPermission("create-reconciliation")}>
              <AddReconciliationBtn updateFront={setReconciliation} />
            </If>
          </div>
        </div>

        <Filters onFilter={handleFilter} />

        {state === "loading" ? (
          <CreateLoaderTable cols={6} lines={6} />
        ) : (
          <TableData
            financialTransactions={reconciliation.data}
            updateFront={setReconciliation}
            dataApi={reconciliation}
            pageActions={pageActions}
            onSort={handleSort}
            sortConfig={sortConfig}
          />
        )}
      </If>
    </Main>
  );
};
